import {yupResolver} from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import UndoIcon from '@mui/icons-material/Undo';
import {Box, Button, Grid, Tab, Tabs} from '@mui/material';
import React, {useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useSelector} from 'react-redux';
import {i18n} from 'src/i18n';
import authSelectors from 'src/modules/auth/authSelectors';
import yupFormSchemas from 'src/modules/shared/yup/yupFormSchemas';
import terceroEnumerators from 'src/modules/tercero/terceroEnumerators';
import {magicFlexBox} from 'src/theme/schemes/CustomTheme';
import ActivoAutocompleteFormItem from 'src/view/activo/autocomplete/ActivoAutocompleteFormItem';
import ClasificacionTerceroAutocompleteFormItem from 'src/view/clasificacionTercero/autocomplete/ClasificacionTerceroAutocompleteFormItem';
import ContactoAutocompleteFormItem from 'src/view/contacto/autocomplete/ContactoAutocompleteFormItem';
import GestorDocumentalAutocompleteFormItem from 'src/view/gestorDocumental/autocomplete/GestorDocumentalAutocompleteFormItem';
import ServicioAutocompleteFormItem from 'src/view/servicio/autocomplete/ServicioAutocompleteFormItem';
import GoogleAutocompleteFormItem from 'src/view/shared/form/items/GoogleAutocompleteFormItem';
import InputFormItem from 'src/view/shared/form/items/InputFormItem';
import InputNumberFormItem from 'src/view/shared/form/items/InputNumberFormItem';
import SelectFormItem from 'src/view/shared/form/items/SelectFormItem';
import FormWrapper, {FormButtons} from 'src/view/shared/styles/FormWrapper';
import * as yup from 'yup';

const schema = yup.object().shape({
  nombre: yupFormSchemas.string(i18n('entities.tercero.fields.nombre'), {
    required: true,
  }),
  razonSocial: yupFormSchemas.string(i18n('entities.tercero.fields.razonSocial'), {}),
  codigo: yupFormSchemas.string(i18n('entities.tercero.fields.codigo'), {
    required: true,
  }),
  tipo: yupFormSchemas.enumerator(i18n('entities.tercero.fields.tipo'), {
    options: terceroEnumerators.tipo,
  }),
  paginaWeb: yupFormSchemas.string(i18n('entities.tercero.fields.paginaWeb'), {}),
  clasificacion: yupFormSchemas.relationToMany(i18n('entities.tercero.fields.clasificacion'), {}),
  direccion: yupFormSchemas.string(i18n('entities.tercero.fields.direccion'), {}),
  ciudad: yupFormSchemas.string(i18n('entities.tercero.fields.ciudad'), {}),
  region: yupFormSchemas.string(i18n('entities.tercero.fields.region'), {}),
  codigoArea: yupFormSchemas.string(i18n('entities.tercero.fields.codigoArea'), {}),
  pais: yupFormSchemas.string(i18n('entities.tercero.fields.pais'), {}),
  ubicacionGoogle: yupFormSchemas.string(i18n('entities.tercero.fields.ubicacionGoogle'), {}),
  latitud: yupFormSchemas.decimal(i18n('entities.tercero.fields.latitud'), {}),
  longitud: yupFormSchemas.decimal(i18n('entities.tercero.fields.longitud'), {}),
  telefonoPrincipal: yupFormSchemas.integer(i18n('entities.tercero.fields.telefonoPrincipal'), {}),
  telefonoSecundario: yupFormSchemas.integer(i18n('entities.tercero.fields.telefonoSecundario'), {}),
  email: yupFormSchemas.string(i18n('entities.tercero.fields.email'), {}),
  fax: yupFormSchemas.integer(i18n('entities.tercero.fields.fax'), {}),
  telefonoSMS: yupFormSchemas.integer(i18n('entities.tercero.fields.telefonoSMS'), {}),
  contactos: yupFormSchemas.relationToMany(i18n('entities.tercero.fields.contactos'), {}),
  servicios: yupFormSchemas.relationToMany(i18n('entities.tercero.fields.servicios'), {}),
  activosVinculados: yupFormSchemas.relationToMany(i18n('entities.tercero.fields.activosVinculados'), {}),
  documentosAdjuntos: yupFormSchemas.relationToMany(i18n('entities.perfilEmpleado.fields.documentosAdjuntos'), {}),
});

function TabPanel(props) {
  const {children, value, index, simpleView, ...other} = props;

  return (
    <Box
      sx={
        {
          ...magicFlexBox,
          display: value !== index ? 'none' : 'flex',
        } as any
      }
      role="tabpanel"
      style={{width: '100%', marginTop: simpleView ? -30 : undefined}}
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}>
      {value === index && (
        <Box
          sx={
            {
              ...magicFlexBox,
              overflow: 'auto',
            } as any
          }
          p={0}
          pt={2}>
          {children}
        </Box>
      )}
    </Box>
  );
}

function TerceroForm(props) {
  const contractorsView = props.contractorsView;
  const tenantUser = useSelector(authSelectors.selectTenantUser);
  const isBlumar = useSelector(authSelectors.selectIsBlumar);
  const [tabValue, setTabValue] = useState(0);

  const [initialValues] = useState(() => {
    const record = props.record || {};

    return {
      razonSocial: record.razonSocial || record.nombre,
      nombre: record.nombre,
      codigo: record.codigo,
      tipo: record.tipo,
      clasificacion: record.clasificacion || [],
      activosVinculados: record.activosVinculados || [],
      paginaWeb: record.paginaWeb,
      direccion: record.direccion,
      ciudad: record.ciudad,
      region: record.region,
      codigoArea: record.codigoArea,
      pais: record.pais,
      ubicacionGoogle: record.ubicacionGoogle,
      latitud: record.latitud,
      longitud: record.longitud,
      telefonoPrincipal: record.telefonoPrincipal,
      telefonoSecundario: record.telefonoSecundario,
      email: record.email,
      fax: record.fax,
      telefonoSMS: record.telefonoSMS,
      contactos: record.contactos || [],
      servicios: record.servicios || [],
      documentosAdjuntos: record.documentosAdjuntos || [],
    };
  });

  const form = useForm({
    resolver: yupResolver(schema),
    mode: 'all',
    defaultValues: initialValues as any,
    shouldUnregister: false,
  });

  const onSubmit = (values) => {
    props.onSubmit(props.record?.id, values);
  };

  const onReset = () => {
    Object.keys(initialValues).forEach((key) => {
      form.setValue(key, initialValues[key]);
    });
  };

  const {saveLoading, modal} = props;
  
  function a11yProps(index) {
    return {
      id: `tercero-tab-${index}`,
      'aria-controls': `tercero-tabpanel-${index}`,
    };
  }

  const handleChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <FormWrapper>
      <FormProvider {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          style={{
            ...magicFlexBox,
            flexGrow: 1,
            overflow: 'auto',
          }}>
          <Box
            sx={
              {
                ...magicFlexBox,
                flexGrow: 1,
                bgcolor: 'background.paper',
              } as any
            }>
            
            <Tabs
              orientation="horizontal"
              variant="scrollable"
              value={tabValue}
              onChange={handleChange}
              aria-label="tercero-wizard"
              textColor="secondary"
              indicatorColor="secondary"
              sx={{
                borderRight: 1,
                borderColor: 'divider',
              }}>
              <Tab label="General" {...a11yProps(0)} />
              <Tab label="Adjuntos" {...a11yProps(1)} />
            </Tabs>
            
            <TabPanel value={tabValue} index={0}>
              <Grid spacing={2} container>
            <Grid item lg={7} md={8} sm={12} xs={12}>
              <InputFormItem name="codigo" label={i18n('entities.tercero.fields.codigo')} required={true} />
            </Grid>
            <Grid item lg={7} md={8} sm={12} xs={12}>
              <InputFormItem name="razonSocial" label={i18n('entities.tercero.fields.razonSocial')} required={true} autoFocus />
            </Grid>
            <Grid item lg={7} md={8} sm={12} xs={12}>
              <InputFormItem name="nombre" label={i18n('entities.tercero.fields.nombre')} required={true} autoFocus />
            </Grid>
            <Grid item lg={7} md={8} sm={12} xs={12}>
              <SelectFormItem
                name="tipo"
                label={i18n('entities.tercero.fields.tipo')}
                options={terceroEnumerators.tipo.map((value) => ({
                  value,
                  label: i18n(`entities.tercero.enumerators.tipo.${value}`),
                }))}
                required={false}
              />
            </Grid>
            {form.watch('tipo') === 'Proveedor' && (
              <Grid item lg={7} md={8} sm={12} xs={12}>
                <ServicioAutocompleteFormItem
                  name="servicios"
                  label={i18n('entities.tercero.fields.servicios')}
                  required={false}
                  showCreate={!props.modal}
                  mode="multiple"
                  disabled={props.disabled}
                />
              </Grid>
            )}
            <Grid item lg={7} md={8} sm={12} xs={12}>
              <ClasificacionTerceroAutocompleteFormItem
                name="clasificacion"
                label={i18n('entities.tercero.fields.clasificacion')}
                required={false}
                showCreate={!props.modal}
                mode="multiple"
                disabled={props.disabled}
              />
            </Grid>
            {!contractorsView && (
              <Grid item lg={7} md={8} sm={12} xs={12}>
                <ActivoAutocompleteFormItem
                  name="activosVinculados"
                  label={i18n('entities.tercero.fields.activosVinculados')}
                  required={false}
                  showCreate={!props.modal}
                  mode="multiple"
                  disabled={props.disabled}
                />
              </Grid>
            )}
            {!contractorsView && (
              <Grid item lg={7} md={8} sm={12} xs={12}>
                <InputFormItem
                  disabled={props.disabled}
                  name="paginaWeb"
                  label={i18n('entities.tercero.fields.paginaWeb')}
                  required={false}
                />
              </Grid>
            )}
            <Grid item lg={7} md={8} sm={12} xs={12}>
              <InputFormItem
                disabled={props.disabled}
                name="direccion"
                label={i18n('entities.tercero.fields.direccion')}
                required={false}
              />
            </Grid>
            {!contractorsView && (
              <Grid item lg={7} md={8} sm={12} xs={12}>
                <InputFormItem
                  disabled={props.disabled}
                  name="ciudad"
                  label={i18n('entities.tercero.fields.ciudad')}
                  required={false}
                />
              </Grid>
            )}
            {!contractorsView && (
              <Grid item lg={7} md={8} sm={12} xs={12}>
                <InputFormItem
                  disabled={props.disabled}
                  name="region"
                  label={i18n('entities.tercero.fields.region')}
                  required={false}
                />
              </Grid>
            )}
            {!contractorsView && (
              <Grid item lg={7} md={8} sm={12} xs={12}>
                <InputFormItem
                  disabled={props.disabled}
                  name="codigoArea"
                  label={i18n('entities.tercero.fields.codigoArea')}
                  required={false}
                />
              </Grid>
            )}
            {!contractorsView && (
              <Grid item lg={7} md={8} sm={12} xs={12}>
                <InputFormItem
                  disabled={props.disabled}
                  name="pais"
                  label={i18n('entities.tercero.fields.pais')}
                  required={false}
                />
              </Grid>
            )}
            {!contractorsView && (
              <Grid item lg={7} md={8} sm={12} xs={12}>
                <GoogleAutocompleteFormItem
                  disabled={props.disabled}
                  name="ubicacionGoogle"
                  label={i18n('entities.ubicacion.fields.ubicacionGoogle')}
                  required={false}
                  defaultCenter={{
                    lat: -36.8269882,
                    lng: -73.0497665,
                  }}
                  center={{
                    lat: form.watch('latitud'),
                    lng: form.watch('longitud'),
                  }}
                  zoom={14}
                  enableMap={true}
                />
              </Grid>
            )}
            <Grid item lg={7} md={8} sm={12} xs={12}>
              <InputNumberFormItem
                disabled={props.disabled}
                name="telefonoPrincipal"
                label={i18n('entities.tercero.fields.telefonoPrincipal')}
                required={false}
              />
            </Grid>
            {!contractorsView && (
              <Grid item lg={7} md={8} sm={12} xs={12}>
                <InputNumberFormItem
                  disabled={props.disabled}
                  name="telefonoSecundario"
                  label={i18n('entities.tercero.fields.telefonoSecundario')}
                  required={false}
                />
              </Grid>
            )}
            <Grid item lg={7} md={8} sm={12} xs={12}>
              <InputFormItem
                disabled={props.disabled}
                name="email"
                label={i18n('entities.tercero.fields.email')}
                required={false}
              />
            </Grid>
            {!contractorsView && (
              <Grid item lg={7} md={8} sm={12} xs={12}>
                <InputNumberFormItem
                  disabled={props.disabled}
                  name="fax"
                  label={i18n('entities.tercero.fields.fax')}
                  required={false}
                />
              </Grid>
            )}
            {!contractorsView && (
              <Grid item lg={7} md={8} sm={12} xs={12}>
                <InputNumberFormItem
                  disabled={props.disabled}
                  name="telefonoSMS"
                  label={i18n('entities.tercero.fields.telefonoSMS')}
                  required={false}
                />
              </Grid>
            )}
            {!contractorsView && (
              <Grid item lg={7} md={8} sm={12} xs={12}>
                <ContactoAutocompleteFormItem
                  disabled={props.disabled}
                  name="contactos"
                  label={i18n('entities.tercero.fields.contactos')}
                  required={false}
                  showCreate={!props.modal}
                  mode="multiple"
                />
              </Grid>
            )}
          </Grid>
            </TabPanel>
            
            <TabPanel value={tabValue} index={1}>
              <Grid spacing={2} container>
                <Grid item lg={props.drawer ? 12 : 7} md={8} sm={12} xs={12}>
                  <GestorDocumentalAutocompleteFormItem
                    name="documentosAdjuntos"
                    label={i18n('entities.perfilEmpleado.fields.documentosAdjuntos')}
                    disabled={props.disabled}
                    required={false}
                    showCreate={!props.modal}
                    defaultprops={{tipo: 'Documento'}}
                    showButton={true}
                    mode="multiple"
                    disableAutocomplete
                  />
                </Grid>
              </Grid>
            </TabPanel>
          </Box>
        </form>
        <FormButtons
          style={{
            flexDirection: modal ? 'row-reverse' : undefined,
          }}>
          <Button
            variant="contained"
            color="primary"
            disabled={saveLoading}
            type="button"
            onClick={form.handleSubmit(onSubmit)}
            startIcon={<SaveIcon />}
            size="small">
            {i18n('common.save')}
          </Button>

          <Button disabled={saveLoading} onClick={onReset} type="button" startIcon={<UndoIcon />} size="small">
            {i18n('common.reset')}
          </Button>

          {props.onCancel ? (
            <Button disabled={saveLoading} onClick={() => props.onCancel()} type="button" startIcon={<CloseIcon />} size="small">
              {i18n('common.cancel')}
            </Button>
          ) : null}
        </FormButtons>
      </FormProvider>
    </FormWrapper>
  );
}

export default TerceroForm;
